/**
 * @fileoverview TraceContext class for managing trace state
 * @module TraceContext
 */

import { generateTraceId, generateSpanId, generateRequestId } from './generators.js';
import { formatTraceparent } from './headers.js';
import { TracingConfig, shouldSample } from './config.js';
import { TRACE_HEADERS } from './constants.js';

/**
 * Trace context state for a request
 */
export class TraceContext {
  /**
   * Create a new TraceContext
   * @param {Object} [options={}] - Context options
   * @param {string} [options.traceId] - Trace ID (generated if not provided)
   * @param {string} [options.spanId] - Span ID (generated if not provided)
   * @param {string} [options.parentSpanId] - Parent span ID
   * @param {string} [options.requestId] - Request ID (generated if not provided)
   * @param {string} [options.tenantId] - Tenant ID for multi-tenant apps
   * @param {boolean} [options.sampled] - Whether trace is sampled
   * @param {string} [options.traceState] - W3C tracestate value
   * @param {string} [options.correlationId] - Page-scoped correlation id
   *   (`x-correlation-id`); null on the page context itself
   */
  constructor(options = {}) {
    this.traceId = options.traceId || generateTraceId();
    this.spanId = options.spanId || generateSpanId();
    this.parentSpanId = options.parentSpanId || null;
    this.requestId = options.requestId || generateRequestId();
    this.tenantId = options.tenantId || null;
    // Respect explicit sampled value, otherwise use configuration
    this.sampled = options.sampled !== undefined ? options.sampled : shouldSample();
    this.traceState = options.traceState || '';
    this.correlationId = options.correlationId || null;
  }

  /**
   * Create a child span context
   * @returns {TraceContext} New trace context with this span as parent
   */
  createChildSpan() {
    return new TraceContext({
      traceId: this.traceId,
      spanId: generateSpanId(),
      parentSpanId: this.spanId,
      requestId: this.requestId,
      tenantId: this.tenantId,
      sampled: this.sampled,
      traceState: this.traceState,
      correlationId: this.correlationId,
    });
  }

  /**
   * Get the traceparent header value
   * @returns {string} Traceparent header
   */
  getTraceparent() {
    return formatTraceparent(this.traceId, this.spanId, this.sampled);
  }

  /**
   * Convert to headers object for HTTP requests
   * @returns {Object} Headers object
   */
  toHeaders() {
    const headers = {
      [TRACE_HEADERS.TRACEPARENT]: this.getTraceparent(),
      [TRACE_HEADERS.REQUEST_ID]: this.requestId,
    };

    // Include debug headers based on configuration
    if (TracingConfig.includeDebugHeaders) {
      headers[TRACE_HEADERS.TRACE_ID] = this.traceId;
      headers[TRACE_HEADERS.SPAN_ID] = this.spanId;
    }

    if (this.traceState) {
      headers[TRACE_HEADERS.TRACESTATE] = this.traceState;
    }

    if (this.tenantId) {
      headers[TRACE_HEADERS.TENANT_ID] = this.tenantId;
    }

    if (this.correlationId) {
      headers[TRACE_HEADERS.CORRELATION_ID] = this.correlationId;
    }

    return headers;
  }
}
